import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { buildingServicesApi, ConstructionRegistration, ConstructionRegistrationQuery, BuildingServiceSettings } from '../../api/buildingServicesApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { useAuth } from '../../contexts/AuthContext';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { CONSTRUCTION_REGISTRATION_PERMISSIONS } from '../../constants/functionPermissions';
import { formatVietnamDateTime } from '../../utils/vietnamDateTime';
import { formatVndAmount } from '../../utils/vndFormat';
import { ServiceAdvanceBookingSettings } from '../../components/building-services/ServiceAdvanceBookingSettings';
import ConstructionDepositModal from '../../components/modals/ConstructionDepositModal';
import ConstructionHistoryModal from '../../components/modals/ConstructionHistoryModal';
import LedgerVoucherModal from '../../components/modals/LedgerVoucherModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

interface FilterOptions {
  buildingId: string;
  apartmentId: string;
  status: string;
  startDate: string;
  endDate: string;
  search: string;
  page: number;
  limit: number;
}

const ConstructionRegistrationList: React.FC = () => {
  const { user } = useAuth();
  const { canCreate, canEdit } = useCrudPermissions(CONSTRUCTION_REGISTRATION_PERMISSIONS);
  const [registrations, setRegistrations] = useState<ConstructionRegistration[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [serviceSettings, setServiceSettings] = useState<BuildingServiceSettings | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [depositModal, setDepositModal] = useState<{
    registration: ConstructionRegistration;
    action: 'collect' | 'refund';
  } | null>(null);
  const [voucherModal, setVoucherModal] = useState<{ entryId: string; title: string } | null>(null);
  const [historyModal, setHistoryModal] = useState<ConstructionRegistration | null>(null);

  const [filters, setFilters] = useState<FilterOptions>({
    buildingId: '',
    apartmentId: '',
    status: '',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 25
  });

  const [settingsForm, setSettingsForm] = useState({
    isActive: true,
    regulations: '',
    depositAmount: '',
    advanceBookingDays: { min: 0, max: 60 },
    staffAdvanceBookingDays: { min: 0, max: 60 },
  });

  const statusOptions = [
    { value: '', label: 'Tất cả trạng thái' },
    { value: 'PENDING_CONFIRMATION', label: 'Chờ xác nhận' },
    { value: 'CONFIRMED', label: 'Đã xác nhận' },
    { value: 'IN_CONSTRUCTION', label: 'Đang thi công' },
    { value: 'COMPLETED', label: 'Hoàn thành' },
    { value: 'REJECTED', label: 'Từ chối' },
    { value: 'CANCELLED', label: 'Đã hủy' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_CONFIRMATION': return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800';
      case 'IN_CONSTRUCTION': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      const building = buildings.find(b => b.id === selectedBuildingId);
      setSelectedBuilding(building || null);
      loadApartments();
      loadServiceSettings();
    } else {
      setSelectedBuilding(null);
      setApartments([]);
      setServiceSettings(null);
    }
  }, [selectedBuildingId, buildings]);

  useEffect(() => {
    if (selectedBuildingId) {
      loadRegistrations();
    } else {
      setRegistrations([]);
      setTotal(0);
      setTotalPages(0);
    }
  }, [selectedBuildingId, filters]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      setBuildings(response);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(response, user, prev));
    } catch (error) {
      console.error('Error loading buildings:', error);
      toast.error('Lỗi khi tải danh sách tòa nhà');
    }
  };

  const loadApartments = async () => {
    if (!selectedBuildingId) return;
    
    try {
      const response = await apartmentsApi.getApartments();
      // Filter apartments by building ID
      const filteredApartments = response.filter(apt => apt.buildingId === selectedBuildingId);
      setApartments(filteredApartments);
    } catch (error) {
      console.error('Error loading apartments:', error);
      toast.error('Lỗi khi tải danh sách căn hộ');
    }
  };

  const loadServiceSettings = async () => {
    if (!selectedBuildingId) return;
    
    try {
      const settings = await buildingServicesApi.getServiceSettings(selectedBuildingId, 'CONSTRUCTION');
      setServiceSettings(settings);
      if (settings) {
        setSettingsForm({
          isActive: settings.isActive,
          regulations: settings.regulations || '',
          depositAmount:
            settings.depositAmount != null ? String(settings.depositAmount) : '',
          advanceBookingDays: settings.advanceBookingDays ?? { min: 0, max: 60 },
          staffAdvanceBookingDays: settings.staffAdvanceBookingDays ?? { min: 0, max: 60 },
        });
      }
    } catch (error) {
      console.error('Error loading service settings:', error);
    }
  };

  const loadRegistrations = async () => {
    if (!selectedBuildingId) return;
    
    setLoading(true);
    try {
      const query: ConstructionRegistrationQuery = {
        ...filters,
        buildingId: selectedBuildingId
      };
      
      const response = await buildingServicesApi.getConstructionRegistrations(query);
      setRegistrations(response.data);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / filters.limit));
    } catch (error) {
      console.error('Error loading registrations:', error);
      toast.error('Lỗi khi tải danh sách đăng ký thi công');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (value as number)
    }));
  };

  const clearFilters = () => {
    setFilters({
      buildingId: selectedBuildingId,
      apartmentId: '',
      status: '',
      startDate: '',
      endDate: '',
      search: '',
      page: 1,
      limit: 25
    });
  };

  const handleBuildingChange = (buildingId: string) => {
    selectBuilding(user?.id, buildingId, setSelectedBuildingId);
    setFilters(prev => ({ ...prev, buildingId, apartmentId: '', page: 1 }));
  };

  const saveSettings = async () => {
    if (!selectedBuildingId) return;
    
    try {
      const depositAmount = settingsForm.depositAmount
        ? parseFloat(settingsForm.depositAmount.replace(/\./g, ''))
        : 0;
      const payload = {
        isActive: settingsForm.isActive,
        regulations: settingsForm.regulations,
        depositAmount: Number.isFinite(depositAmount) ? depositAmount : 0,
        advanceBookingDays: settingsForm.advanceBookingDays,
        staffAdvanceBookingDays: settingsForm.staffAdvanceBookingDays,
      };
      if (serviceSettings) {
        await buildingServicesApi.updateServiceSettings(selectedBuildingId, 'CONSTRUCTION', payload);
        toast.success('Cập nhật cài đặt thành công');
      } else {
        await buildingServicesApi.createServiceSettings(selectedBuildingId, {
          serviceType: 'CONSTRUCTION',
          ...payload,
        });
        toast.success('Tạo cài đặt thành công');
      }
      
      setShowSettingsModal(false);
      loadServiceSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Lỗi khi lưu cài đặt');
    }
  };

  const canManage = ['SUPERADMIN', 'STAFF', 'OPERATOR'].includes(user?.role || '');

  const handleConfirmRegistration = async (id: string) => {
    if (!window.confirm('Xác nhận đăng ký này?')) return;
    try {
      await buildingServicesApi.confirmConstruction(id);
      toast.success('Xác nhận đăng ký thành công');
      loadRegistrations();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Lỗi khi xác nhận đăng ký'));
    }
  };

  const handleRejectRegistration = async (id: string) => {
    const rejectionReason = window.prompt('Nhập lý do từ chối:');
    if (!rejectionReason?.trim()) return;
    try {
      await buildingServicesApi.rejectConstruction(id, rejectionReason.trim());
      toast.success('Từ chối đăng ký thành công');
      loadRegistrations();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Lỗi khi từ chối đăng ký'));
    }
  };

  const handleStartConstruction = async (id: string) => {
    if (!window.confirm('Bắt đầu thi công cho đăng ký này?')) return;
    try {
      await buildingServicesApi.startConstruction(id);
      toast.success('Bắt đầu thi công thành công');
      loadRegistrations();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Lỗi khi bắt đầu thi công'));
    }
  };

  const handleCompleteConstruction = async (id: string) => {
    if (!window.confirm('Hoàn thành đăng ký này?')) return;
    try {
      await buildingServicesApi.completeConstruction(id);
      toast.success('Hoàn thành thi công thành công');
      loadRegistrations();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Lỗi khi hoàn thành thi công'));
    }
  };

  const handleCancelRegistration = async (id: string) => {
    if (!window.confirm('Hủy đăng ký này?')) return;
    try {
      await buildingServicesApi.cancelConstruction(id);
      toast.success('Hủy đăng ký thành công');
      loadRegistrations();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Lỗi khi hủy đăng ký'));
    }
  };

  const openDepositVoucher = async (
    registration: ConstructionRegistration,
    kind: 'collect' | 'refund',
  ) => {
    const title = kind === 'collect' ? 'Phiếu thu cọc' : 'Phiếu hoàn cọc';
    let entryId =
      kind === 'collect'
        ? registration.depositCollectLedgerEntryId
        : registration.depositRefundLedgerEntryId;

    if (!entryId) {
      try {
        const fresh = await buildingServicesApi.getConstructionRegistration(registration.id);
        entryId =
          kind === 'collect'
            ? fresh.depositCollectLedgerEntryId
            : fresh.depositRefundLedgerEntryId;
        if (entryId) {
          setRegistrations((prev) =>
            prev.map((row) =>
              row.id === registration.id
                ? {
                    ...row,
                    depositCollectLedgerEntryId: fresh.depositCollectLedgerEntryId,
                    depositRefundLedgerEntryId: fresh.depositRefundLedgerEntryId,
                  }
                : row,
            ),
          );
        }
      } catch {
        toast.error('Không thể tải thông tin phiếu');
        return;
      }
    }

    if (!entryId) {
      toast.error('Chưa tìm thấy phiếu. Kiểm tra đã thu/hoàn trên sổ quỹ.');
      return;
    }

    setVoucherModal({ entryId, title });
  };

  const canCancelRegistration = (registration: ConstructionRegistration) => {
    const isOwner = registration.createdById === user?.id;
    if (registration.status === 'PENDING_CONFIRMATION') {
      return isOwner || canManage;
    }
    if (registration.status === 'CONFIRMED' && !registration.depositPaid) {
      return canManage;
    }
    return false;
  };

  const renderActionButtons = (registration: ConstructionRegistration) => {
    const hasDeposit = (registration.depositAmount ?? 0) > 0;
    const needsCounterCollection =
      canManage &&
      registration.status === 'CONFIRMED' &&
      hasDeposit &&
      !registration.depositPaid;
    const canStart =
      registration.status === 'CONFIRMED' && (!hasDeposit || registration.depositPaid);
    const canRefund =
      canManage &&
      hasDeposit &&
      registration.depositPaid &&
      !registration.depositRefunded &&
      registration.status === 'IN_CONSTRUCTION';
    const canComplete =
      canManage &&
      registration.status === 'IN_CONSTRUCTION' &&
      (!hasDeposit || registration.depositRefunded);

    return (
      <div className="flex flex-wrap gap-x-2 gap-y-1">
        {canEdit && (
          <Link
            to={`/building-services/construction/${registration.id}/edit`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Xem
          </Link>
        )}

        <button
          type="button"
          onClick={() => setHistoryModal(registration)}
          className="text-gray-600 hover:text-gray-800 text-sm"
        >
          Lịch sử
        </button>

        {registration.depositPaid && (
          <button
            type="button"
            onClick={() => openDepositVoucher(registration, 'collect')}
            className="text-cyan-600 hover:text-cyan-800 text-sm"
          >
            Phiếu thu
          </button>
        )}

        {registration.depositRefunded && (
          <button
            type="button"
            onClick={() => openDepositVoucher(registration, 'refund')}
            className="text-teal-600 hover:text-teal-800 text-sm"
          >
            Phiếu hoàn
          </button>
        )}

        {canEdit && canManage && registration.status === 'PENDING_CONFIRMATION' && (
          <button
            type="button"
            onClick={() => handleConfirmRegistration(registration.id)}
            className="text-green-600 hover:text-green-800 text-sm"
          >
            Xác nhận
          </button>
        )}

        {canEdit && canManage && registration.status === 'PENDING_CONFIRMATION' && (
          <button
            type="button"
            onClick={() => handleRejectRegistration(registration.id)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Từ chối
          </button>
        )}

        {canEdit && needsCounterCollection && (
          <button
            type="button"
            onClick={() => setDepositModal({ registration, action: 'collect' })}
            className="text-yellow-600 hover:text-yellow-800 text-sm"
          >
            Thu cọc
          </button>
        )}

        {canEdit && canManage && canStart && (
          <button
            type="button"
            onClick={() => handleStartConstruction(registration.id)}
            className="text-purple-600 hover:text-purple-800 text-sm"
          >
            Bắt đầu
          </button>
        )}

        {canEdit && canRefund && (
          <button
            type="button"
            onClick={() => setDepositModal({ registration, action: 'refund' })}
            className="text-orange-600 hover:text-orange-800 text-sm"
          >
            Hoàn cọc
          </button>
        )}

        {canEdit && canComplete && (
          <button
            type="button"
            onClick={() => handleCompleteConstruction(registration.id)}
            className="text-green-600 hover:text-green-800 text-sm"
          >
            Hoàn thành
          </button>
        )}

        {canEdit && canCancelRegistration(registration) && (
          <button
            type="button"
            onClick={() => handleCancelRegistration(registration.id)}
            className="text-orange-600 hover:text-orange-800 text-sm"
          >
            Hủy
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Đăng ký thi công</h1>
          <p className="text-gray-600">Quản lý đăng ký thi công trong tòa nhà</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              if (!selectedBuildingId) {
                toast.error('Vui lòng chọn tòa nhà trước khi cài đặt');
                return;
              }
              setShowSettingsModal(true);
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ⚙️ Cài đặt
          </button>
          {canCreate && (
            <Link
              to={selectedBuildingId ? `/building-services/construction/add?buildingId=${selectedBuildingId}` : '#'}
              onClick={(e) => {
                if (!selectedBuildingId) {
                  e.preventDefault();
                  toast.error('Vui lòng chọn tòa nhà trước khi thêm đăng ký thi công');
                }
              }}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedBuildingId 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-400 text-white cursor-not-allowed'
              }`}
            >
              + Thêm đăng ký thi công
            </Link>
          )}
        </div>
      </div>

      {/* Building Selection */}
      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Chọn tòa nhà:</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => handleBuildingChange(e.target.value)}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map(building => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedBuildingId && (
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Bộ lọc</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="text-blue-600 hover:text-blue-800"
              >
                {showFilters ? 'Ẩn bộ lọc' : 'Hiện bộ lọc'}
              </button>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Căn hộ
                  </label>
                  <select
                    value={filters.apartmentId}
                    onChange={(e) => handleFilterChange('apartmentId', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tất cả căn hộ</option>
                    {apartments.map(apartment => (
                      <option key={apartment.id} value={apartment.id}>
                        {apartment.code} - {apartment.ownerName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trạng thái
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Từ ngày
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Đến ngày
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tìm kiếm
                  </label>
                  <input
                    type="text"
                    placeholder="Tìm theo tên đơn vị, số điện thoại, nội dung..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2 flex space-x-3">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Xóa bộ lọc
                  </button>
                  <select
                    value={filters.limit}
                    onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10 kết quả</option>
                    <option value={25}>25 kết quả</option>
                    <option value={50}>50 kết quả</option>
                    <option value={100}>100 kết quả</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Registration List */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Danh sách đăng ký ({total} kết quả)
              </h3>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Đang tải...</p>
              </div>
            ) : registrations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Không có đăng ký thi công nào
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          STT
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Căn hộ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Đơn vị thi công
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Thời gian thi công
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trạng thái
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tiền cọc
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ngày tạo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Thao tác
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {registrations.map((registration, index) => (
                        <tr key={registration.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(filters.page - 1) * filters.limit + index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {registration.apartment.code}
                            </div>
                            <div className="text-sm text-gray-500">
                              {registration.apartment.ownerName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {registration.contractorName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {registration.contractorPhone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              {new Date(registration.startDate).toLocaleDateString('vi-VN')}
                              {registration.startTime && <span className="text-blue-600 ml-1">{registration.startTime}</span>}
                            </div>
                            <div className="text-gray-500">
                              đến {new Date(registration.endDate).toLocaleDateString('vi-VN')}
                              {registration.endTime && <span className="text-blue-600 ml-1">{registration.endTime}</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(registration.status)}`}>
                              {getStatusText(registration.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {registration.depositAmount ? (
                              <div>
                                <div>{formatVndAmount(registration.depositAmount)}</div>
                                <div className="text-xs text-gray-500">
                                  {registration.depositPaid ? '✅ Đã thu' : '⏳ Chưa thu'}
                                  {registration.depositRefunded && ' / 🔄 Đã hoàn'}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">Không có</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatVietnamDateTime(registration.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {renderActionButtons(registration)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Hiển thị {(filters.page - 1) * filters.limit + 1} đến {Math.min(filters.page * filters.limit, total)} trong tổng số {total} kết quả
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                        disabled={filters.page <= 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Trước
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = Math.max(1, Math.min(totalPages - 4, filters.page - 2)) + i;
                        return (
                          <button
                            key={page}
                            onClick={() => handleFilterChange('page', page)}
                            className={`px-3 py-1 border rounded text-sm ${
                              filters.page === page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handleFilterChange('page', Math.min(totalPages, filters.page + 1))}
                        disabled={filters.page >= totalPages}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Sau
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Cài đặt dịch vụ thi công
              </h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settingsForm.isActive}
                    onChange={(e) => setSettingsForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Đang hoạt động
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tiền cọc mặc định (VNĐ)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={settingsForm.depositAmount}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, '');
                    const formatted = raw
                      ? Number(raw).toLocaleString('vi-VN')
                      : '';
                    setSettingsForm((prev) => ({
                      ...prev,
                      depositAmount: formatted,
                    }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0 = không thu cọc"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Cư dân đăng ký trên app sẽ thanh toán số tiền này. Đặt 0 để tắt thu cọc.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quy định thi công
                </label>
                <textarea
                  value={settingsForm.regulations}
                  onChange={(e) => setSettingsForm(prev => ({ ...prev, regulations: e.target.value }))}
                  rows={10}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập quy định thi công của tòa nhà..."
                />
              </div>

              <ServiceAdvanceBookingSettings
                value={{
                  advanceBookingDays: settingsForm.advanceBookingDays,
                  staffAdvanceBookingDays: settingsForm.staffAdvanceBookingDays,
                }}
                onChange={(next) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    advanceBookingDays: next.advanceBookingDays,
                    staffAdvanceBookingDays: next.staffAdvanceBookingDays,
                  }))
                }
              />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={saveSettings}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {depositModal && (
        <ConstructionDepositModal
          registration={depositModal.registration}
          action={depositModal.action}
          onClose={() => setDepositModal(null)}
          onCompleted={() => loadRegistrations()}
        />
      )}
      {voucherModal && (
        <LedgerVoucherModal
          entryId={voucherModal.entryId}
          title={voucherModal.title}
          onClose={() => setVoucherModal(null)}
        />
      )}
      {historyModal && (
        <ConstructionHistoryModal
          registration={historyModal}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
};

export default ConstructionRegistrationList; 